/**
 * Angle Calculation Utilities for Bike Fitting Analysis
 */

import { SimpleFrame } from '../modules/ProcessingPipeline';

interface Point {
  x: number;
  y: number;
  confidence?: number;
}

interface Keypoints {
  rightShoulder: Point;
  rightElbow: Point;
  rightWrist: Point;
  rightHip: Point;
  rightKnee: Point;
  rightAnkle: Point;
}

/**
 * Calculate angle at vertex p2 formed by points p1-p2-p3
 * Returns angle in degrees
 */
export function calculateAngle(
  p1: Point,
  p2: Point,  // vertex
  p3: Point
): number {
  // Create vectors from vertex to other points
  const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
  const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };
  
  // Calculate dot product and magnitudes
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
  const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
  
  // Calculate angle in radians, then convert to degrees
  const angleRad = Math.acos(dot / (mag1 * mag2));
  return angleRad * (180 / Math.PI);
}

/**
 * Calculate knee angle (hip-knee-ankle)
 */
export function calculateKneeAngle(keypoints: Keypoints): number {
  return calculateAngle(
    keypoints.rightHip,
    keypoints.rightKnee,
    keypoints.rightAnkle
  );
}

/**
 * Calculate hip angle (shoulder-hip-knee)
 */
export function calculateHipAngle(keypoints: Keypoints): number {
  return calculateAngle(
    keypoints.rightShoulder,
    keypoints.rightHip,
    keypoints.rightKnee
  );
}

/**
 * Calculate trunk angle: angle of hip→shoulder line relative to horizontal.
 * 0° = fully horizontal (aero), 90° = fully upright.
 */
export function calculateTrunkAngle(keypoints: Keypoints): number {
  const dx = keypoints.rightShoulder.x - keypoints.rightHip.x;
  const dy = keypoints.rightShoulder.y - keypoints.rightHip.y;
  // Negate dy because canvas Y increases downward
  return Math.abs(Math.atan2(-dy, Math.abs(dx)) * (180 / Math.PI));
}

/**
 * Calculate elbow angle (shoulder-elbow-wrist)
 */
export function calculateElbowAngle(keypoints: Keypoints): number {
  return calculateAngle(
    keypoints.rightShoulder,
    keypoints.rightElbow,
    keypoints.rightWrist
  );
}

/**
 * Average keypoint positions across multiple frames
 */
export function averageKeypoints(frames: SimpleFrame[]): Keypoints {
  const validFrames = frames.filter(f => f.keypoints && f.keypoints.rightAnkle);
  
  if (validFrames.length === 0) {
    throw new Error('No valid frames with keypoints to average');
  }
  
  const avgKeypoints: any = {};
  const keypointNames = ['rightShoulder', 'rightElbow', 'rightWrist', 'rightHip', 'rightKnee', 'rightAnkle'];
  
  keypointNames.forEach(name => {
    let sumX = 0;
    let sumY = 0;
    let sumConf = 0;
    let count = 0;

    validFrames.forEach(frame => {
      const kp = frame.keypoints[name];
      if (kp && kp.confidence > 0.5) {
        sumX += kp.x;
        sumY += kp.y;
        sumConf += kp.confidence;
        count++;
      }
    });

    if (count > 0) {
      avgKeypoints[name] = {
        x: sumX / count,
        y: sumY / count,
        confidence: sumConf / count
      };
    }
  });

  const missing = keypointNames.filter(name => !avgKeypoints[name]);
  if (missing.length > 0) {
    throw new Error(
      `Could not detect body position: low confidence for ${missing.join(', ')}. ` +
      'Make sure your full body (including upper body) is visible throughout the video.'
    );
  }

  return avgKeypoints as Keypoints;
}

/**
 * Find frame with keypoints closest to target keypoints
 * Returns the index in the original frames array
 */
export function findClosestFrame(
  taggedFrames: SimpleFrame[],
  targetKeypoints: Keypoints,
  allFrames: SimpleFrame[]
): number {
  let minDistance = Infinity;
  let closestFrameIndex = -1;
  
  taggedFrames.forEach(frame => {
    if (!frame.keypoints) return;
    
    // Calculate euclidean distance across all keypoints
    let distance = 0;
    const keypointNames = ['rightShoulder', 'rightElbow', 'rightWrist', 'rightHip', 'rightKnee', 'rightAnkle'];
    
    keypointNames.forEach(name => {
      const kp1 = frame.keypoints[name];
      const kp2 = targetKeypoints[name as keyof Keypoints];
      
      if (kp1 && kp2 && kp1.confidence > 0.5) {
        const dx = kp1.x - kp2.x;
        const dy = kp1.y - kp2.y;
        distance += Math.sqrt(dx * dx + dy * dy);
      }
    });
    
    if (distance < minDistance) {
      minDistance = distance;
      closestFrameIndex = allFrames.indexOf(frame);
    }
  });
  
  return closestFrameIndex;
}
